import * as Sentry from '@sentry/react';

/**
 * observability.ts (pwa-offline-first Slice 7, task 7.9 / design §9) — the
 * ONLY place in `offline/*` that imports `@sentry/react` directly. Every
 * function here is a thin, pure(-ish) wrapper so `foreground-drainer.ts`
 * (application layer) stays testable with plain fakes, exactly the same
 * hexagonal boundary `build-real-checkin-context.ts` keeps for the API
 * client — Sentry is wired in at the composition root
 * (`foreground-drainer-instance.ts`), never called from inside the pure
 * drain loop directly.
 *
 * CRITICAL — no PII, ever: no plate, no phone, no name, no address crosses
 * into ANY of these calls. Only error CODES (`OutboxLastError.code`,
 * already server-controlled and non-identifying) and counts.
 */

export interface SyncOutcomeEvent {
  kind: 'success' | 'retry' | 'reject' | 'needs-auth';
  /** The classified error's `code` — omitted for a clean `success`. */
  errorCode?: string;
}

/** A breadcrumb per sync attempt outcome (design §9's sync-outcome observability). */
export function recordSyncOutcome(event: SyncOutcomeEvent): void {
  Sentry.addBreadcrumb({
    category: 'offline.sync',
    level: event.kind === 'reject' ? 'warning' : 'info',
    message: `sync ${event.kind}`,
    ...(event.errorCode ? { data: { code: event.errorCode } } : {}),
  });
}

/** Sentry tag `offline.pending` (design §9) — the CURRENT total unsynced count. */
export function tagPendingCount(count: number): void {
  Sentry.setTag('offline.pending', count);
}

/**
 * A warning EVENT (not just a breadcrumb) per rejected item, fingerprinted
 * by reason code so every `qr_tag_conflict` (etc.) groups into one Sentry
 * issue instead of flooding the issue list with one-off events (design §9:
 * "Warning event per rejected item (fingerprint = reason code)").
 */
export function reportRejectedItem(code: string): void {
  Sentry.captureMessage(`offline check-in rejected: ${code}`, {
    level: 'warning',
    fingerprint: ['offline-rejected', code],
    tags: { 'offline.reject_code': code },
  });
}

/** `offline.queue_stale` (design §9) — the oldest pending item is older than 1h. */
export function reportQueueStale(oldestAgeMs: number): void {
  Sentry.captureMessage('offline queue stale', {
    level: 'warning',
    fingerprint: ['offline-queue-stale'],
    tags: { 'offline.queue_stale': true },
    extra: { oldestAgeMs },
  });
}

/**
 * Fifth adversarial review precedent (Slice 6 round 5, HIGH #1) — a plan
 * not covering damage reports is exactly the kind of operator-invisible
 * pattern Sentry should surface in aggregate, so an org whose plan silently
 * drops incident photos on every sync becomes VISIBLE to the team, not just
 * to that one operator on that one screen.
 */
export function reportIncidentPhotosDropped(code: string): void {
  Sentry.captureMessage(`incident photos dropped: ${code}`, {
    level: 'warning',
    fingerprint: ['offline-incident-photos-dropped', code],
    tags: { 'offline.incident_photos_dropped_reason': code },
  });
}

/** Sentry tag `sw.version` (design §9) — the currently-active service worker's version. */
export function tagServiceWorkerVersion(version: string): void {
  Sentry.setTag('sw.version', version);
}
